/**
 * Tour Utilities
 *
 * Helper functions for managing tour initialization and element detection.
 */

/**
 * Wait for DOM elements to be available with retries.
 *
 * Checks for the presence of required tour target elements before starting tours.
 * Uses exponential backoff with a maximum wait time.
 *
 * @param selectors - Array of CSS selectors to check for
 * @param options - Configuration options
 * @param options.maxWaitMs - Maximum time to wait in milliseconds (default: 5000)
 * @param options.initialDelayMs - Initial delay before first check (default: 100)
 * @param options.maxRetries - Maximum number of retry attempts (default: 10)
 * @returns Promise that resolves when all elements are found, or rejects if timeout
 */
export async function waitForTourElements(
  selectors: string[],
  options: {
    maxWaitMs?: number;
    initialDelayMs?: number;
    maxRetries?: number;
  } = {}
): Promise<void> {
  const { maxWaitMs = 5000, initialDelayMs = 100, maxRetries = 10 } = options;

  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    let attempt = 0;

    const checkElements = () => {
      const allFound = selectors.every((selector) => {
        const element = document.querySelector(selector);
        return element !== null;
      });

      if (allFound) {
        resolve();
        return;
      }

      attempt++;
      const elapsed = Date.now() - startTime;

      if (elapsed >= maxWaitMs || attempt >= maxRetries) {
        reject(
          new Error(
            `Tour elements not found after ${elapsed}ms. Missing selectors: ${selectors
              .filter((s) => !document.querySelector(s))
              .join(", ")}`
          )
        );
        return;
      }

      // Exponential backoff: 100ms, 200ms, 400ms, etc., capped at 500ms
      const delay = Math.min(initialDelayMs * Math.pow(2, attempt - 1), 500);
      setTimeout(checkElements, delay);
    };

    // Start checking after initial delay
    setTimeout(checkElements, initialDelayMs);
  });
}

/**
 * Check if tour elements are currently available in the DOM.
 *
 * @param selectors - Array of CSS selectors to check
 * @returns True if all elements are found, false otherwise
 */
export function areTourElementsReady(selectors: string[]): boolean {
  return selectors.every((selector) => document.querySelector(selector) !== null);
}

/**
 * Get session-based tour completion key.
 *
 * Combines localStorage key with session identifier to prevent
 * tour from repeating in the same browser session.
 *
 * @param baseKey - Base localStorage key (e.g., "app_tour_completed")
 * @returns Session-scoped key
 */
export function getSessionTourKey(baseKey: string): string {
  // Use sessionStorage to track if tour was shown in this session
  // This prevents repeats even if localStorage is cleared
  const sessionId = sessionStorage.getItem("tour_session_id") || Date.now().toString();
  sessionStorage.setItem("tour_session_id", sessionId);
  return `${baseKey}_${sessionId}`;
}

/**
 * Check if tour was completed in current session.
 *
 * @param baseKey - Base localStorage key
 * @returns True if tour was completed in this session
 */
export function hasCompletedTourInSession(baseKey: string): boolean {
  const sessionKey = getSessionTourKey(baseKey);
  return sessionStorage.getItem(sessionKey) === "true";
}

/**
 * Mark tour as completed in current session.
 *
 * @param baseKey - Base localStorage key
 */
export function markTourCompletedInSession(baseKey: string): void {
  const sessionKey = getSessionTourKey(baseKey);
  sessionStorage.setItem(sessionKey, "true");
}
